import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Select, DatePicker } from "antd";
import DataHelper from "../../helpers/DataHelper";
import moment from "moment";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const { Option } = Select;

const MediaLogFilter = forwardRef(
  ({ onFilterChange = () => {}, mode = "media", parentProps }, ref) => {
    const { lang } = useProLanguage();
    const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).mediaLog;
    const [filters, setFilters] = useState({
      usage: [],
      expiry: [],
      dateRange: null, // Store selected date range
    });
    const [loading, setLoading] = useState(false);
    const [screenDatas, setScreenDatas] = useState([]);
    const [fileDatas, setFileDatas] = useState([]);

    const setDateRange = useCallback((dates) => {
      try {
        console.log("setDateRange " + JSON.stringify(dates));
        setFilters((prev) => ({
          ...prev,
          dateRange: dates.map((d) => moment(d)),
        }));
      } catch (e) {
        console.log("setDateRange error " + e);
      }
    }, []);

    // 🧠 Expose functions to parent via ref
    useImperativeHandle(ref, () => ({
      getCurrentFilters: () => filters,
      resetFilters: () => {
        const reset = { usage: [], expiry: [], dateRange: null };
        setFilters(reset);
        onFilterChange(reset);
      },
      setDateRange: setDateRange,
    }));

    useEffect(() => {
      if (parentProps && parentProps.selectedBrandData) {
        reloadScreens();
        getFiles();
      }
    }, [parentProps]);

    const getFiles = () => {
      const { selectedBrandData } = parentProps;

      if (!selectedBrandData) {
        return;
      }
      const params = {
        brandid: selectedBrandData.BrandID,
      };

      DataHelper.getFiles(params, parentProps).then((data) => {
        console.log("getFiles " + JSON.stringify(data));
        setFileDatas(data);
      });
    };
    const reloadScreens = () => {
      const { selectedBrandData } = parentProps;
      console.log("reloadScreens");

      setLoading(true);
      DataHelper.getScreens(
        { brandid: selectedBrandData.BrandID },
        parentProps,
      ).then((data) => {
        setScreenDatas(data);
        setLoading(false);
      });
    };

    const startDate = filters.dateRange?.[0] || null;
    const endDate = filters.dateRange?.[1] || null;

    const handleStartDateChange = (date) => {
      const end = filters.dateRange?.[1] || null;
      if (date && end) {
        handleFilterChange("dateRange", [date, end]);
      } else {
        setFilters((prev) => ({
          ...prev,
          dateRange: date ? [date, end] : null,
        }));
      }
    };

    const handleEndDateChange = (date) => {
      const start = filters.dateRange?.[0] || null;
      if (start && date) {
        handleFilterChange("dateRange", [start, date]);
      } else {
        setFilters((prev) => ({
          ...prev,
          dateRange: date ? [start, date] : null,
        }));
      }
    };

    const handleFilterChange = (key, value) => {
      console.log("handleFilterChange " + key + " " + value);
      const newFilters = { ...filters, [key]: value };
      setFilters(newFilters);
      onFilterChange(newFilters); // Notify parent
    };

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "flex-end",
        }}
      >
        {/* Date Start */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t.filterDateStart}
          </label>
          <DatePicker
            allowClear
            disabledDate={(d) =>
              d.isAfter(moment(), "day") ||
              (endDate && d.isAfter(endDate, "day"))
            }
            value={startDate}
            onChange={handleStartDateChange}
            size="middle"
            style={{ width: 155, borderRadius: 8, height: 36 }}
          />
        </div>

        {/* Date End */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t.filterDateEnd}
          </label>
          <DatePicker
            allowClear
            disabledDate={(d) =>
              d.isAfter(moment(), "day") ||
              (startDate && d.isBefore(startDate, "day"))
            }
            value={endDate}
            onChange={handleEndDateChange}
            size="middle"
            style={{ width: 155, borderRadius: 8, height: 36 }}
          />
        </div>

        {/* Media filter */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            flex: 1,
            minWidth: 200,
          }}
        >
          <label
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t.filterMedia}
          </label>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder={t.filterAllMedia}
            onChange={(value) => handleFilterChange("files", value)}
            allowClear
            loading={loading}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {fileDatas.map((file) => (
              <Select.Option key={file.FileID} value={file.FileID}>
                {file.FileName}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Screen filter */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            minWidth: 180,
          }}
        >
          <label
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t.filterScreens}
          </label>
          <Select
            mode="multiple"
            style={{ minWidth: 180 }}
            placeholder={t.filterAllScreens}
            onChange={(value) => handleFilterChange("screens", value)}
            allowClear
            optionFilterProp="children"
            loading={loading}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {screenDatas.map((screen) => (
              <Select.Option key={screen.ScreenID} value={screen.ScreenID}>
                {screen.ScreenName}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
    );
  },
);

export default MediaLogFilter;
